import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Close,
  Person,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { THEME_COLORS, USER_ROLES } from '../../constants';

const initialFormData = {
  nombre: '',
  correo: '',
  celular: '',
  password: '',
  confirmPassword: '',
  rol: USER_ROLES.COLLABORATOR,
};

const UserFormModalComponent = ({ open, onClose, onSubmit, entity, isEditing }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEditing && entity) {
        setFormData({
          nombre: entity.nombre || '',
          correo: entity.correo || '',
          celular: entity.celular || '',
          rol: entity.rol || USER_ROLES.COLLABORATOR,
          password: '',
          confirmPassword: '',
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
      setSubmitError('');
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [entity, isEditing, open]);

  const handleInputChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido.';
    if (!formData.correo.trim()) {
      newErrors.correo = 'El correo es requerido.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo)) {
      newErrors.correo = 'El formato del correo no es válido.';
    }
    if (!formData.celular.trim()) newErrors.celular = 'El celular es requerido.';
    
    if (!isEditing || formData.password) {
      if (!formData.password) {
        newErrors.password = 'La contraseña es requerida para nuevos usuarios.';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres.';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden.';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditing]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!validate()) return;
    
    const dataToSubmit = { ...formData };
    if (isEditing && !dataToSubmit.password) {
      delete dataToSubmit.password;
      delete dataToSubmit.confirmPassword;
    }
    
    try {
      await onSubmit(dataToSubmit);
      // El cierre del modal y reseteo lo maneja el hook usePageOperations
    } catch (error) {
      setSubmitError(error.message || 'Ocurrió un error al guardar el usuario.');
    }
  }, [formData, isEditing, onSubmit, validate]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          boxShadow: `0 8px 32px ${THEME_COLORS.SHADOW_LIGHT}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: 4,
          paddingBottom: 3,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Person sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '1.25rem',
                }}
              >
                {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {isEditing ? 'Modifica los datos del usuario.' : 'Agrega un nuevo usuario al sistema.'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: THEME_COLORS.TEXT_SECONDARY, '&:hover': { backgroundColor: THEME_COLORS.BACKGROUND_PAPER, color: THEME_COLORS.TEXT_PRIMARY } }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ padding: 4, paddingTop: 3 }}>
        <form id="user-form" onSubmit={handleSubmit}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
              {submitError}
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, fontSize: '1rem', marginBottom: 2 }}>
                Información Personal
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nombre Completo"
                placeholder="Ej: José Pérez"
                value={formData.nombre}
                onChange={handleInputChange('nombre')}
                error={!!errors.nombre}
                helperText={errors.nombre || 'El nombre del usuario que se mostrará en el sistema.'}
                fullWidth
                required
                autoComplete="name"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Correo Electrónico"
                placeholder="Ej: jose.perez@email.com"
                value={formData.correo}
                onChange={handleInputChange('correo')}
                error={!!errors.correo}
                helperText={errors.correo || 'El correo para iniciar sesión.'}
                fullWidth
                required
                autoComplete="email"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Número de Celular"
                placeholder="Ej: +54 9 11 1234 5678"
                value={formData.celular}
                onChange={handleInputChange('celular')}
                error={!!errors.celular}
                helperText={errors.celular || 'El número para notificaciones y contacto.'}
                fullWidth
                required
                autoComplete="tel"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600, fontSize: '1rem', marginBottom: 2, mt: 2 }}>
                Configuración de Acceso
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}>
                <InputLabel id="rol-label">Rol del Usuario</InputLabel>
                <Select
                  labelId="rol-label"
                  label="Rol del Usuario"
                  name="rol"
                  value={formData.rol}
                  onChange={handleInputChange('rol')}
                >
                  <MenuItem value={USER_ROLES.ADMIN}>Administrador</MenuItem>
                  <MenuItem value={USER_ROLES.COLLABORATOR}>Colaborador</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={isEditing ? 'Nueva Contraseña (Opcional)' : 'Contraseña'}
                type={showPassword ? 'text' : 'password'}
                placeholder="Dejar en blanco para no cambiar"
                value={formData.password}
                onChange={handleInputChange('password')}
                error={!!errors.password}
                helperText={errors.password || 'Mínimo 6 caracteres.'}
                fullWidth
                required={!isEditing}
                autoComplete="new-password"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={isEditing ? 'Confirmar Nueva Contraseña' : 'Confirmar Contraseña'}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Repetir la contraseña"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                fullWidth
                required={!isEditing}
                autoComplete="new-password"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputLabel-root': { color: THEME_COLORS.TEXT_SECONDARY } }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          p: { xs: 2, sm: 3 },
          gap: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 120 },
            color: THEME_COLORS.TEXT_SECONDARY,
            borderColor: THEME_COLORS.BORDER_LIGHT,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          form="user-form"
          variant="contained"
          size="large"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 140 },
            backgroundColor: THEME_COLORS.TEXT_PRIMARY,
            color: THEME_COLORS.BACKGROUND_PAPER,
            borderRadius: '12px',
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#1a1a1a',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {isEditing ? 'Actualizar Usuario' : 'Crear Usuario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UserFormModalComponent.displayName = 'UserFormModal';
const UserFormModal = memo(UserFormModalComponent);

export default UserFormModal; 